import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { loyaltySession } from '../../utils/session';
import { restoreLoyaltySession } from '../../utils/loyaltySessionRestore';
import {
  fetchReservationOptions,
  fetchReservationAvailability,
  fetchMyReservations,
  createLoyaltyReservation,
  cancelLoyaltyReservation,
} from '../../services/loyaltyReservationsApi';
import { ApiError } from '../../utils/laravelError';
import {
  findUnseenApprovedReservations,
  markReservationApprovalSeen,
} from '../../utils/reservationApprovalNotice';
import {
  findUnseenDeniedReservations,
  markReservationDenialSeen,
} from '../../utils/reservationDenialNotice';
import { getDenialReasonText, hasDenialReason } from '../../utils/reservationDenialReason';
import { filterUpcomingReservations } from '../../utils/reservationDates';
import './LoyaltyReservation.css';

const STATUS_CHIP_SX = {
  pending: {
    bgcolor: 'rgba(255, 107, 0, 0.15)',
    color: '#ffb366',
    border: '1px solid rgba(255, 107, 0, 0.45)',
  },
  confirmed: {
    bgcolor: 'rgba(76, 175, 80, 0.15)',
    color: '#81c784',
    border: '1px solid rgba(76, 175, 80, 0.45)',
  },
  denied: {
    bgcolor: 'rgba(244, 67, 54, 0.18)',
    color: '#ff8a80',
    border: '1px solid rgba(244, 67, 54, 0.55)',
  },
  cancelled: {
    bgcolor: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  no_show: {
    bgcolor: 'rgba(244, 67, 54, 0.18)',
    color: '#ff8a80',
    border: '1px solid rgba(244, 67, 54, 0.55)',
  },
  completed: {
    bgcolor: 'rgba(33, 150, 243, 0.15)',
    color: '#64b5f6',
    border: '1px solid rgba(33, 150, 243, 0.45)',
  },
};

const sectionTitleSx = {
  color: '#ff6b00',
  fontWeight: 600,
  mb: 2.5,
  fontSize: '1.1rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const backButtonSx = {
  color: '#ff6b00',
  borderColor: 'rgba(255, 107, 0, 0.3)',
  '&:hover': {
    bgcolor: 'rgba(255, 107, 0, 0.1)',
    borderColor: '#ff6b00',
  },
};

const submitButtonSx = {
  bgcolor: '#ff6b00',
  padding: '12px 24px',
  fontSize: '0.95rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  mt: 0.5,
  '&:hover': {
    bgcolor: '#e55f00',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)',
  },
  transition: 'all 0.3s ease',
};

const cancelButtonSx = {
  color: '#ff6b00',
  borderColor: 'rgba(255, 107, 0, 0.5)',
  textTransform: 'none',
  borderRadius: '8px',
  '&:hover': {
    borderColor: '#ff6b00',
    bgcolor: 'rgba(255, 107, 0, 0.1)',
  },
};

function getDateLocale(language) {
  return language === 'bg' ? 'bg-BG' : 'en-US';
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildDateOptions(maxDaysAhead, language) {
  const locale = getDateLocale(language);
  const options = [];
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  for (let offset = 0; offset <= maxDaysAhead; offset += 1) {
    const date = new Date(cursor);
    date.setDate(cursor.getDate() + offset);

    options.push({
      value: toDateInputValue(date),
      label: date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    });
  }

  return options;
}

function formatDateTime(iso, language) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(getDateLocale(language), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTimeSlot(iso, language) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(getDateLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function LoyaltyReservation() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [maxDaysAhead, setMaxDaysAhead] = useState(7);
  const [maxDuration, setMaxDuration] = useState(120);
  const [maxPartySize, setMaxPartySize] = useState(20);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [myReservations, setMyReservations] = useState([]);
  const [approvalDialogRes, setApprovalDialogRes] = useState(null);
  const [denialDialogRes, setDenialDialogRes] = useState(null);
  const dismissedApprovalIdsRef = useRef(new Set());
  const dismissedDenialIdsRef = useRef(new Set());
  const [form, setForm] = useState({
    party_size: 2,
    wants_playstation: 'no',
    date: '',
    reserved_at: '',
    notes: '',
  });

  const wantsPlaystation = form.wants_playstation === 'yes';

  const loadReservations = useCallback(async () => {
    try {
      const data = await fetchMyReservations();
      setMyReservations(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setMyReservations([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const user = await restoreLoyaltySession({ forceValidate: true });
      if (!user) {
        navigate('/loyalty/login');
        return;
      }

      try {
        const meta = await fetchReservationOptions();
        setMaxDaysAhead(meta.max_days_ahead || 7);
        setMaxDuration(meta.max_duration_minutes || 120);
        setMaxPartySize(meta.max_party_size || 20);
        await loadReservations();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate('/loyalty/login');
          return;
        }
        setError(err.message || 'Failed to load reservation options.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate, loadReservations]);

  const visibleReservations = useMemo(
    () => filterUpcomingReservations(myReservations),
    [myReservations],
  );

  useEffect(() => {
    if (loading) return undefined;

    const interval = setInterval(() => {
      loadReservations();
    }, 60000);

    return () => clearInterval(interval);
  }, [loading, loadReservations]);

  useEffect(() => {
    if (approvalDialogRes || denialDialogRes) return;

    const [nextApproved] = findUnseenApprovedReservations(myReservations)
      .filter((reservation) => !dismissedApprovalIdsRef.current.has(String(reservation.id)));

    if (nextApproved) {
      setApprovalDialogRes(nextApproved);
      return;
    }

    const [nextDenied] = findUnseenDeniedReservations(myReservations)
      .filter((reservation) => !dismissedDenialIdsRef.current.has(String(reservation.id)));

    if (nextDenied) {
      setDenialDialogRes(nextDenied);
    }
  }, [myReservations, approvalDialogRes, denialDialogRes]);

  const handleAcknowledgeApproval = () => {
    if (!approvalDialogRes) return;

    const closedId = String(approvalDialogRes.id);
    dismissedApprovalIdsRef.current.add(closedId);
    markReservationApprovalSeen(closedId);
    setApprovalDialogRes(null);

    const [nextApproved] = findUnseenApprovedReservations(myReservations)
      .filter((reservation) => !dismissedApprovalIdsRef.current.has(String(reservation.id)));

    if (nextApproved) {
      window.setTimeout(() => setApprovalDialogRes(nextApproved), 300);
      return;
    }

    const [nextDenied] = findUnseenDeniedReservations(myReservations)
      .filter((reservation) => !dismissedDenialIdsRef.current.has(String(reservation.id)));

    if (nextDenied) {
      window.setTimeout(() => setDenialDialogRes(nextDenied), 300);
    }
  };

  const handleAcknowledgeDenial = () => {
    if (!denialDialogRes) return;

    const closedId = String(denialDialogRes.id);
    dismissedDenialIdsRef.current.add(closedId);
    markReservationDenialSeen(closedId);
    setDenialDialogRes(null);

    const [nextDenied] = findUnseenDeniedReservations(myReservations)
      .filter((reservation) => !dismissedDenialIdsRef.current.has(String(reservation.id)));

    if (nextDenied) {
      window.setTimeout(() => setDenialDialogRes(nextDenied), 300);
    }
  };

  useEffect(() => {
    const loadSlots = async () => {
      if (!form.date) {
        setSlots([]);
        return;
      }

      setSlotsLoading(true);
      try {
        const data = await fetchReservationAvailability({
          date: form.date,
          party_size: Number(form.party_size),
          wants_playstation: wantsPlaystation ? 1 : 0,
          duration_minutes: maxDuration,
        });
        setSlots(data.slots || []);
        if (form.reserved_at && !data.slots?.includes(form.reserved_at)) {
          setForm((prev) => ({ ...prev, reserved_at: '' }));
        }
      } catch (err) {
        setSlots([]);
        setError(err.message || 'Could not load available times.');
      } finally {
        setSlotsLoading(false);
      }
    };

    loadSlots();
  }, [form.date, form.party_size, form.wants_playstation, form.reserved_at, wantsPlaystation, maxDuration]);

  const dateOptions = useMemo(
    () => buildDateOptions(maxDaysAhead, i18n.language),
    [maxDaysAhead, i18n.language],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!form.date || !form.reserved_at) {
      setError(t('loyalty.reservations.errors.requiredFields', 'Please fill in guests, date and time.'));
      return;
    }

    setSubmitting(true);
    try {
      await createLoyaltyReservation({
        party_size: Number(form.party_size),
        wants_playstation: wantsPlaystation,
        reserved_at: form.reserved_at,
        duration_minutes: maxDuration,
        notes: form.notes || null,
      });
      setSuccess(t('loyalty.reservations.successSubmitted', 'Request sent! A waiter will confirm shortly.'));
      setForm((prev) => ({ ...prev, reserved_at: '', notes: '' }));
      await loadReservations();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/loyalty/login');
        return;
      }
      setError(err.message || t('loyalty.reservations.errors.submitFailed', 'Could not create reservation.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setError(null);
    try {
      await cancelLoyaltyReservation(id);
      await loadReservations();
      setSuccess(t('loyalty.reservations.cancelled', 'Reservation cancelled.'));
    } catch (err) {
      setError(err.message || 'Could not cancel reservation.');
    }
  };

  if (loading) {
    return (
      <Box className="loyalty-reservation-container">
        <CircularProgress sx={{ color: '#ff6b00' }} />
      </Box>
    );
  }

  return (
    <Box className="loyalty-reservation-container">
      <Card className="loyalty-reservation-card">
        <CardContent>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255, 107, 0, 0.2)',
          }}
          >
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
              {t('loyalty.reservations.title', 'Table Reservation')}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/loyalty/profile')}
              size="small"
              sx={backButtonSx}
            >
              {t('loyalty.settings.back', 'Back')}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, lineHeight: 1.6 }}>
            {t('loyalty.reservations.subtitle', 'Book a table for up to {{hours}} hours, up to {{days}} days ahead.', { hours: maxDuration / 60, days: maxDaysAhead })}
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box className="reservation-section">
            <Typography variant="h6" sx={sectionTitleSx}>
              {t('loyalty.reservations.newReservation', 'New reservation')}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} className="reservation-form">
              <TextField
                type="number"
                label={t('loyalty.reservations.partySize', 'Number of guests')}
                value={form.party_size}
                onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                inputProps={{ min: 1, max: maxPartySize }}
                fullWidth
                size="small"
                required
              />

              <TextField
                select
                label={t('loyalty.reservations.playstation', 'PlayStation')}
                value={form.wants_playstation}
                onChange={(e) => setForm({ ...form, wants_playstation: e.target.value, reserved_at: '' })}
                fullWidth
                size="small"
                required
              >
                <MenuItem value="yes">{t('loyalty.reservations.playstationYes', 'Yes, we want PlayStation')}</MenuItem>
                <MenuItem value="no">{t('loyalty.reservations.playstationNo', 'No PlayStation')}</MenuItem>
              </TextField>

              <TextField
                select
                label={t('loyalty.reservations.date', 'Date')}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value, reserved_at: '' })}
                fullWidth
                size="small"
                required
              >
                <MenuItem disabled value="">
                  {t('loyalty.reservations.selectDate', 'Select a date')}
                </MenuItem>
                {dateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t('loyalty.reservations.timeSlot', 'Time (2h slot)')}
                value={form.reserved_at}
                onChange={(e) => setForm({ ...form, reserved_at: e.target.value })}
                fullWidth
                size="small"
                required
                disabled={!form.date || slotsLoading}
                helperText={slotsLoading ? t('loyalty.reservations.loadingSlots', 'Loading times…') : undefined}
              >
                {slots.length === 0 && !slotsLoading ? (
                  <MenuItem disabled value="">
                    {t('loyalty.reservations.noSlots', 'No available slots')}
                  </MenuItem>
                ) : (
                  slots.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {formatTimeSlot(slot, i18n.language)}
                    </MenuItem>
                  ))
                )}
              </TextField>

              <TextField
                label={t('loyalty.reservations.notes', 'Notes (optional)')}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                multiline
                rows={2}
                fullWidth
                size="small"
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={submitButtonSx}
              >
                {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('loyalty.reservations.submit', 'Request reservation')}
              </Button>
            </Box>
          </Box>

          {visibleReservations.length > 0 && (
            <>
              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Box className="reservation-section">
                <Typography variant="h6" sx={sectionTitleSx}>
                  {t('loyalty.reservations.myReservations', 'My reservations')}
                </Typography>

                <Box className="loyalty-reservation-list">
                  {visibleReservations.map((res) => (
                    <Box key={res.id} className="reservation-item">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                          {formatDateTime(res.reserved_at, i18n.language)}
                          {' · '}
                          {res.table?.number != null
                            ? t('loyalty.reservations.tableLabel', 'Table {{number}}', { number: res.table.number })
                            : t('loyalty.reservations.pendingTable', 'Table to be assigned')}
                        </Typography>
                        <Chip
                          size="small"
                          label={t(`loyalty.reservations.status.${res.status}`, res.status)}
                          sx={STATUS_CHIP_SX[res.status] || STATUS_CHIP_SX.cancelled}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t('loyalty.reservations.partySizeShort', '{{count}} guests', { count: res.party_size })}
                        {' · '}
                        {res.wants_playstation
                          ? t('loyalty.reservations.playstationYesShort', 'PlayStation')
                          : t('loyalty.reservations.playstationNoShort', 'No PlayStation')}
                      </Typography>
                      {res.status === 'denied' && hasDenialReason(res) && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            color: '#ff8a80',
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            border: '1px solid rgba(244, 67, 54, 0.25)',
                            borderRadius: '8px',
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          {t('loyalty.reservations.denialReasonLabel', 'Reason')}
                          {': '}
                          {getDenialReasonText(res, t)}
                        </Typography>
                      )}
                      {['pending', 'confirmed'].includes(res.status) && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ...cancelButtonSx, mt: 1.5 }}
                          onClick={() => handleCancel(res.id)}
                        >
                          {t('loyalty.reservations.cancel', 'Cancel')}
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(approvalDialogRes)}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        className="loyalty-approval-dialog"
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.98)',
            borderRadius: '16px',
            border: '1px solid rgba(76, 175, 80, 0.35)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#81c784',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid rgba(76, 175, 80, 0.2)',
            pb: 2,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
          {t('loyalty.reservations.approvalDialog.title', 'Your reservation is confirmed')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, color: '#fff' }}>
          <Typography
            variant="body1"
            sx={{ mb: 2.5, lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.92)' }}
          >
            {t(
              'loyalty.reservations.approvalDialog.message',
              'Great news — your table reservation has been approved. We look forward to welcoming you at Shisha Vibes and making your visit memorable.',
            )}
          </Typography>
          {approvalDialogRes && (
            <Box
              sx={{
                bgcolor: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.25)',
                borderRadius: '12px',
                padding: '1.25rem',
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.75 }}>
                {t('loyalty.reservations.approvalDialog.details', '{{date}} · {{count}} guests', {
                  date: formatDateTime(approvalDialogRes.reserved_at, i18n.language),
                  count: approvalDialogRes.party_size,
                })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                {approvalDialogRes.table?.number != null
                  ? t('loyalty.reservations.approvalDialog.tableAssigned', 'Table {{number}} is reserved for you.', {
                      number: approvalDialogRes.table.number,
                    })
                  : t(
                      'loyalty.reservations.approvalDialog.tablePending',
                      'Your table will be ready for you on arrival.',
                    )}
              </Typography>
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.75)', fontStyle: 'italic' }}
          >
            {t('loyalty.reservations.approvalDialog.closing', 'See you soon!')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={handleAcknowledgeApproval}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4caf50',
              color: '#fff',
              py: 1.25,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#43a047',
              },
            }}
          >
            {t('loyalty.reservations.approvalDialog.cta', 'Ok, I understood')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(denialDialogRes)}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        className="loyalty-denial-dialog"
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.98)',
            borderRadius: '16px',
            border: '1px solid rgba(244, 67, 54, 0.35)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#ff8a80',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid rgba(244, 67, 54, 0.2)',
            pb: 2,
          }}
        >
          <HighlightOffOutlinedIcon sx={{ fontSize: 32 }} />
          {t('loyalty.reservations.denialDialog.title', 'Reservation declined')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, color: '#fff' }}>
          <Typography
            variant="body1"
            sx={{ mb: 2.5, lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.92)' }}
          >
            {t(
              'loyalty.reservations.denialDialog.message',
              'Unfortunately, we could not confirm your table reservation. Please see the reason below.',
            )}
          </Typography>
          {denialDialogRes && (
            <Box
              sx={{
                bgcolor: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.25)',
                borderRadius: '12px',
                padding: '1.25rem',
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('loyalty.reservations.denialDialog.details', '{{date}} · {{count}} guests', {
                  date: formatDateTime(denialDialogRes.reserved_at, i18n.language),
                  count: denialDialogRes.party_size,
                })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.55)', mb: 0.5 }}>
                {t('loyalty.reservations.denialDialog.reasonLabel', 'Reason')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.92)', lineHeight: 1.6 }}>
                {getDenialReasonText(denialDialogRes, t)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={handleAcknowledgeDenial}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#e53935',
              color: '#fff',
              py: 1.25,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#c62828',
              },
            }}
          >
            {t('loyalty.reservations.denialDialog.cta', 'Ok, I understood')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoyaltyReservation;
